<template>
  <div class="order-create-container">
    <base-loader v-if="dataIsLoading" />

    <div v-else-if="order" class="order-form">
      <!-- Навигация -->
      <base-nav
        :title="order.cargo_name ? `Заказ #${order.number}: ${order.cargo_name}` : `Заказ #${order.number}`"
        :show-back="true"
        back-fallback="Orders"
        :back-fallback-pattern="/\/orders/"
      >
        <base-primary-button
          v-if="!editingOrderInfo"
          class="nav-btn"
          icon="share"
          @click="createSharing"
        >
          Поделиться
        </base-primary-button>
        <base-button
          class="nav-btn"
          v-if="editingOrderInfo"
          @click="cancelEditOrderInfo"
        >
          Отмена
        </base-button>
      </base-nav>

      <!-- Вкладки -->
      <BaseTabs
        v-model="activeTab"
        :tabs="computedTabs"
        :context-menu-items="getTabContextMenuItems"
        :show-default-context-item="getTabShowDefaultItem"
        :prevent-update="
          (key) => key === 'trips' && orderTrips.length === 1
        "
        class="order-tabs-wrapper"
        @tab-click="handleTabClick"
        @middle-click="handleMiddleClick"
      />

      <!-- Вкладки "Заказ" -->
      <div v-show="activeTab === 'order'" class="tab-content">
        <div class="card order-block">
          <div v-if="!editingOrderInfo">
            <div class="block-nav">
              <h3 class="nav-title">Основная информация</h3>
              <div class="nav-actions">
                <EdoCreateDocumentButton
                  :source-order-id="id"
                  :source-organization-inn="orderShipperInn"
                  source-return-tab="order"
                />
                <base-button
                  class="nav-btn"
                  icon="edit"
                  @click="startEditingOrderInfo"
                >
                  Редактировать
                </base-button>
                <base-button
                  class="nav-btn"
                  type="button"
                  :disabled="loading"
                  :icon="order.enabled ? 'pause' : 'play'"
                  @click="toggleStatus"
                >
                  {{ order.enabled ? "Отключить" : "Включить" }}
                </base-button>
              </div>
            </div>

            <!-- Режим отображения основной информации -->
            <OrderMainInfo
              :order="order"
              :editOrderForm="editOrderForm"
              :orderTrips="orderTrips"
              :company-id="companyId"
              @create-trip="createTrip"
              @go-to-trip-details="goToTripDetails"
              @status-changed="handleStatusChange"
              @payment-status-changed="handlePaymentStatusChange"
              @order-type-changed="handleOrderTypeChange"
            />
          </div>

          <!-- Режим редактирования основной информации -->
          <form @submit.prevent="saveOrderInfo" v-else>
            <OrderEditMainInfo
              :formData="editOrderForm"
              @update:formData="updateFormData"
              ref="formRef"
            />

            <!-- Кнопки -->
            <div class="form-actions">
              <base-primary-button
                type="submit"
                :disabled="isSubmitting"
                class="form-btn"
              >
                <span v-if="isSubmitting">Сохранение...</span>
                <span v-else>Сохранить</span>
              </base-primary-button>
              <base-button
                type="button"
                @click="cancelEditOrderInfo"
                class="form-btn"
                :disabled="isSubmitting"
              >
                Отмена
              </base-button>
            </div>
          </form>
        </div>

        <!-- Пользовательские поля -->
        <RequisiteList
          :id="this.id"
          :allowed_models="['Order']"
          :requisitable_type="'Order'"
        />

        <!-- Точки заказа -->
        <OrderPoints :id="id" :companyId="companyId" />
      </div>

      <!-- Вкладка "Рейсы" -->
      <div v-show="activeTab === 'trips'" class="tab-content">
        <OrderTripsList
          :trips="orderTrips"
          :orderId="id"
          @create-trip="createTrip"
          @go-to-trip-details="goToTripDetails"
          @add-trip="addTripToOrder"
          @remove-trip="removeTripFromOrder"
        />
      </div>

      <!-- Вкладка "Грузы" -->
      <div v-show="activeTab === 'cargos'" class="tab-content">
        <Step2Cargo
          :order-id="id"
          :show-back-action="false"
          submit-label="Сохранить"
          :proceed-on-save="false"
          @saved="handleCargoSaved"
        />
      </div>

      <div v-show="activeTab === 'invoices'" class="tab-content">
        <OrderInvoiceList :order-id="id" :company-id="companyId" />
      </div>

      <div v-show="activeTab === 'documents'" class="tab-content">
        <TemplateList :targetId="id" />
      </div>

      <div v-show="activeTab === 'edo_documents'" class="tab-content">
        <OrderEdoDocumentsTab
          :order-id="id"
          :source-organization-inn="orderShipperInn"
          source-return-tab="edo_documents"
          @count-change="orderEdoDocumentsCount = $event"
        />
      </div>
    </div>
    <div v-else class="no-data no-data-margin">Заказ не найден</div>

    <BaseDialog
      :visible="showDialog"
      title="Создание рейса"
      message="Создать рейс для заказа?"
      :actions="[
        { key: 'create', label: 'Создать', type: 'primary' },
        { key: 'close', label: 'Отмена', type: 'secondary' },
      ]"
      @close="showDialog = false"
      @action="handleActions"
    />

  </div>
</template>

<script>
import { mapState } from "pinia";
import { useCounterpartyStore } from "@/stores/counterpartyStore";
import { useOrderInvoiceStore } from "@/stores/orderInvoiceStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useOrderStore } from "@/stores/orderStore";
import { useTripStore } from "@/stores/tripStore";
import { useTripCargoStore } from "@/stores/tripCargoStore";

import ordersApi from "@/services/api/ordersApi";
import tripsApi from "@/services/api/tripsApi";

import { getCompanyId } from "@/utils/userUtils";
import { formatDateTimeLocal } from "@/utils/dateUtils";
import { getAndRemoveQueryParam } from "@/utils/urlUtils";
import { buildOrderUpdatePayload } from "@/utils/orderFormUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";

import OrderMainInfo from "@/components/Order/OrderMainInfo.vue";
import EdoCreateDocumentButton from "@/components/Edo/CreateDocumentButton.vue";
import OrderEditMainInfo from "@/components/Order/OrderEditMainInfo.vue";
import OrderEdoDocumentsTab from "@/components/Order/OrderEdoDocumentsTab.vue";
import OrderTripsList from "@/components/Order/OrderTripsList.vue";
import RequisiteList from "@/components/Requisite/RequisiteList.vue";
import OrderPoints from "@/components/Order/OrderPoints.vue";
import TemplateList from "@/components/Template/TemplateList.vue";
import OrderInvoiceList from "@/components/Invoice/OrderInvoiceList.vue";
import Step2Cargo from "@/components/Order/Step2Cargo.vue";

const GEAR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="3"></circle>
  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
</svg>`;

const NEW_TAB_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
  <polyline points="15 3 21 3 21 9"/>
  <line x1="10" y1="14" x2="21" y2="3"/>
</svg>`;

export default {
  name: "OrderDetailsPage",
  components: {
    OrderMainInfo,
    EdoCreateDocumentButton,
    OrderEditMainInfo,
    OrderTripsList,
    RequisiteList,
    OrderPoints,
    TemplateList,
    OrderInvoiceList,
    Step2Cargo,
    OrderEdoDocumentsTab,
  },
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      companyId: getCompanyId(),
      activeTab: "order",

      order: null,
      orderTrips: [],
      orderCargosCount: 0,
      orderEdoDocumentsCount: 0,

      editingOrderInfo: false,

      dataIsLoading: true,
      isSubmitting: false,
      loading: false,

      showDialog: false,

      editOrderForm: {
        number: "",
        status: "pending",
        order_type_id: null,
        payment_status: "not_invoiced",
        order_date: "",
        cargo_name: "",
        cargo_weight: null,
        cargo_volume: null,
        sender: "",
        receiver: "",
        planned_pickup_at: "",
        planned_delivery_at: "",
        actual_pickup_at: "",
        actual_delivery_at: "",
        payment_form: "",
        price: null,
        comment: "",
        pickup_window_start: "",
        pickup_window_end: "",
        delivery_window_start: "",
        delivery_window_end: "",
        min_temperature: null,
        max_temperature: null,
        trip_ids: [],
        responsible_user_id: null,
      },
    };
  },

  watch: {
    id() {
      this.loadData();
    },

    "$route.query": {
      handler(query) {
        this.handleRouteQuery(query);
      },
      immediate: true,
    },

    activeTab(newTab, oldTab) {
      if (newTab != oldTab) {
        this.updateUrlTab(newTab);
      }
    },
  },
  computed: {
    ...mapState(useOrderInvoiceStore, ["invoices", "pagination"]),

    invoicesCount() {
      return this.pagination.count;
    },

    notificationStore() {
      return useNotificationStore();
    },

    orderStore() {
      return useOrderStore();
    },

    tripStore() {
      return useTripStore();
    },

    orderShipperInn() {
      const shipperId = this.order?.shipper_id ||
        (this.order?.order_counterparties || []).find(
          (item) => item.role === "shipper"
        )?.counterparty_id;
      if (!shipperId) return null;

      return useCounterpartyStore().counterparties.find(
        (counterparty) => String(counterparty.id) === String(shipperId)
      )?.inn;
    },

    computedTabs() {
      const hasOneTrip = this.orderTrips.length === 1;
      return [
        { key: "order", label: "Заказ", icon: "📋" },
        {
          key: "trips",
          label: hasOneTrip ? "Рейс" : "Рейсы",
          icon: "🚚",
          badge: hasOneTrip ? null : this.orderTrips.length,
          // Шестерёнка — только когда рейс ровно один
          suffixIcon: hasOneTrip ? GEAR_ICON : null,
        },
        {
          key: "cargos",
          label: "Грузы",
          icon: "📦",
          badge:
            this.orderCargosCount > 0 ? this.orderCargosCount : null,
        },
        {
          key: "invoices",
          label: "Счета",
          icon: "🧾",
          badge: this.invoicesCount > 0 ? this.invoicesCount : null,
        },
        { key: "documents", label: "Документы", icon: "📄" },
        {
          key: "edo_documents",
          label: "ЭДО",
          icon: "🔏",
          badge: this.orderEdoDocumentsCount > 0 ? this.orderEdoDocumentsCount : null,
        },
      ];
    },
  },
  methods: {
    getTabShowDefaultItem(tabKey) {
      if (tabKey === "trips" && this.orderTrips.length === 1) {
        return false;
      }
      return true;
    },

    getTabContextMenuItems(tabKey) {
      if (tabKey === "trips" && this.orderTrips.length === 1) {
        return [
          {
            key: "open-trip-new-tab",
            label: "Открыть рейс в новой вкладке",
            icon: NEW_TAB_ICON,
            action: () =>
              openRouteInNewTab({
                name: "TripDetails",
                params: { id: this.orderTrips[0].id },
              }),
          },
          {
            key: "open-trip-settings-new-tab",
            label: "Открыть настройки рейсов в новой вкладке",
            icon: NEW_TAB_ICON,
            action: () =>
              openRouteInNewTab({
                name: "OrderDetails",
                params: { id: this.id },
                query: { tab: "trips" },
              }),
          },
        ];
      }
      return [];
    },

    handleTabClick({ key, fromSuffix }) {
      if (key === "trips" && this.orderTrips.length === 1) {
        if (fromSuffix) {
          this.activeTab = "trips";
          this.updateUrlTab("trips");
        } else {
          this.goToFirstTrip();
        }
        return;
      }
      this.activeTab = key;
      this.updateUrlTab(key);
    },

    handleMiddleClick({ key, fromSuffix }) {
      if (key === "trips" && this.orderTrips.length === 1) {
        if (fromSuffix) {
          openRouteInNewTab({
            name: "OrderDetails",
            params: { id: this.id },
            query: { tab: "trips" },
          });
        } else {
          openRouteInNewTab({
            name: "TripDetails",
            params: { id: this.orderTrips[0].id },
          });
        }
        return;
      }
      openRouteInNewTab({
        name: "OrderDetails",
        params: { id: this.id },
        query: { ...this.$route.query, tab: key },
      });
    },

    createFormData() {
      this.editOrderForm = {
        number: this.order.number || "",
        status: this.order.status || "pending",
        order_type_id: this.order.order_type
          ? this.order.order_type.id
          : this.order.order_type_id || null,
        payment_status: this.order.payment_status || "not_invoiced",
        cargo_name: this.order.cargo_name || "",
        cargo_weight: this.order.cargo_weight || null,
        cargo_volume: this.order.cargo_volume || null,
        sender: this.order.sender || "",
        receiver: this.order.receiver || "",
        order_date: this.formatDateTimeLocal(this.order.order_date),
        planned_pickup_at: this.formatDateTimeLocal(
          this.order.planned_pickup_at
        ),
        planned_delivery_at: this.formatDateTimeLocal(
          this.order.planned_delivery_at
        ),
        actual_pickup_at: this.formatDateTimeLocal(
          this.order.actual_pickup_at
        ),
        actual_delivery_at: this.formatDateTimeLocal(
          this.order.actual_delivery_at
        ),
        payment_form: this.order.payment_form || "",
        price:
          this.order.price !== null
            ? parseFloat(this.order.price)
            : null,
        comment: this.order.comment || "",
        pickup_window_start: this.formatDateTimeLocal(
          this.order.pickup_window_start
        ),
        pickup_window_end: this.formatDateTimeLocal(
          this.order.pickup_window_end
        ),
        delivery_window_start: this.formatDateTimeLocal(
          this.order.delivery_window_start
        ),
        delivery_window_end: this.formatDateTimeLocal(
          this.order.delivery_window_end
        ),
        min_temperature:
          this.order.min_temperature !== null
            ? parseFloat(this.order.min_temperature)
            : null,
        max_temperature:
          this.order.max_temperature !== null
            ? parseFloat(this.order.max_temperature)
            : null,
        trip_ids: this.order.trip_ids ? [...this.order.trip_ids] : this.order.trip_ids,
        order_counterparties: this.order.order_counterparties,
        responsible_user_id: this.order.responsible_user_id || null,
        waybill_id: this.order.waybill_id || null,
      };
    },

    updateFormData(newData) {
      Object.assign(this.editOrderForm, newData);
    },

    async loadData() {
      this.orderTrips = [];
      this.dataIsLoading = true;

      try {
        const orderRes = await ordersApi.show(
          this.companyId,
          this.id
        );
        this.order = orderRes.data.result;

        // Загружаем контрагентов для режима просмотра
        const counterpartyStore = useCounterpartyStore();
        await counterpartyStore.fetchCounterparties();

        if (this.order.trip_ids && this.order.trip_ids.length > 0) {
          await this.getOrderTrips(this.order.trip_ids);
        }

        await this.loadCargosCount();

        this.createFormData();
      } catch (error) {
        console.error("Ошибка загрузки заказа:", error);
        this.order = null;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async loadCargosCount() {
      try {
        const cargoStore = useTripCargoStore();
        await cargoStore.getCargosRequest({
          "q[order_id]": this.id,
        });
        this.orderCargosCount = cargoStore.pagination.count;
      } catch (error) {
        console.error("Ошибка загрузки количества грузов:", error);
      }
    },

    async handleCargoSaved() {
      await this.loadCargosCount();
    },

    startEditingOrderInfo() {
      this.editingOrderInfo = true;
    },

    createSharing() {
      this.$router.push({
        name: "SharingCreate",
        query: {
          shareableType: "Order",
          shareableId: this.id,
        },
      });
    },

    cancelEditOrderInfo() {
      this.createFormData();

      this.editingOrderInfo = false;
    },

    async handleStatusChange(status) {
      try {
        await this.orderStore.updateStatus(this.id, status);
        this.order = { ...this.order, status };
        this.notificationStore.showSuccess("Статус заказа обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async handlePaymentStatusChange(paymentStatus) {
      try {
        await this.orderStore.updatePaymentStatus(this.id, paymentStatus);
        this.order = { ...this.order, payment_status: paymentStatus };
        this.notificationStore.showSuccess("Статус оплаты обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async handleOrderTypeChange(orderTypeId) {
      try {
        await this.orderStore.updateOrderType(this.id, orderTypeId);
        const type = this.orderStore.orderTypes.find((t) => String(t.id) === orderTypeId);
        this.order = { ...this.order, order_type: type || null };
        this.notificationStore.showSuccess("Тип заказа обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async saveOrderInfo() {
      const formComponent = this.$refs.formRef;

      let isFormValid = true;
      if (
        formComponent &&
        typeof formComponent.validate === "function"
      ) {
        isFormValid = await formComponent.validate();
      }

      if (!isFormValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = buildOrderUpdatePayload(this.editOrderForm);

        await ordersApi.update(this.companyId, this.id, payload);
        await this.loadData();

        this.editingOrderInfo = false;

        this.notificationStore.showSuccess("Заказ успешно обновлен");
      } catch (error) {
        console.error("Ошибка при сохранении заказа:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async createTrip() {
      try {
        const response = await tripsApi.createTripFromOrder(
          this.companyId,
          { order_id: this.id }
        );

        const tripId = response.data.result.id;

        await this.loadData();

        this.notificationStore.showSuccess("Рейс успешно создан");

        this.$router.push({
          name: "TripDetails",
          params: { id: tripId },
        });
      } catch (error) {
        console.error("Ошибка при создании рейса из заказа:", error);
      }
    },

    async toggleStatus() {
      this.loading = true;

      try {
        let response;

        if (this.order.enabled) {
          response = await ordersApi.disable(
            this.companyId,
            this.order.id
          );

          this.notificationStore.showSuccess("Заказ отключен");
        } else {
          response = await ordersApi.enable(
            this.companyId,
            this.order.id
          );

          this.notificationStore.showSuccess("Заказ включен");
        }

        this.order = response.data.result;
      } finally {
        this.loading = false;
      }
    },

    async addTripToOrder(tripId) {
      try {
        const currentTripIds = [...(this.editOrderForm.trip_ids || [])];
        if (!currentTripIds.includes(tripId)) {
          currentTripIds.push(tripId);

          const payload = {
            ...this.editOrderForm,
            trip_ids: currentTripIds.filter((id) => id),
          };

          await ordersApi.update(this.companyId, this.id, payload);
          await this.loadData();

          this.notificationStore.showSuccess(
            "Рейс успешно добавлен к заказу"
          );
        }
      } catch (error) {
        console.error("Ошибка при добавлении рейса к заказу:", error);
      }
    },

    async removeTripFromOrder(tripId) {
      try {
        const currentTripIds = (this.editOrderForm.trip_ids || []).filter(
          (id) => id !== tripId
        );

        const payload = {
          ...this.editOrderForm,
          trip_ids: currentTripIds,
        };

        await ordersApi.update(this.companyId, this.id, payload);
        await this.loadData();

        this.notificationStore.showSuccess(
          "Рейс успешно откреплен от заказа"
        );
      } catch (error) {
        console.error("Ошибка при удалении рейса из заказа:", error);
      }
    },

    async getOrderTrips(tripIds) {
      try {
        const responses = await Promise.all(
          tripIds.map((id) => tripsApi.show(this.companyId, id))
        );

        this.orderTrips = responses.map((res) => {
          const trip = res.data.result;
          return {
            ...trip,
            id: trip.id,
            name: trip.name,
            status: this.tripStore.formatTripStatus(trip.status),
          };
        });
      } catch (error) {
        console.error("Ошибка при загрузке рейсов заказа:", error);
      }
    },

    handleCounterpartiesUpdated(updatedCounterparties) {
      this.counterparties = updatedCounterparties;
    },

    goToFirstTrip() {
      const tripId = this.orderTrips[0].id;
      this.goToTripDetails(tripId);
    },

    updateUrlTab(tabName) {
      // Всегда обновляем URL с табом
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            tab: tabName,
          },
        })
        .catch((err) => {
          // Игнорируем ошибки навигации на тот же маршрут
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
    },

    goToTripDetails(tripId) {
      this.$router.push({
        name: "TripDetails",
        params: { id: tripId },
      });
    },

    handleRouteQuery(query) {
      const showDialog = getAndRemoveQueryParam(
        "showDialog",
        this.$router,
        this.$route
      );
      if (showDialog == "true") {
        this.showDialog = true;
      }

      // Устанавливаем вкладку из URL, если она есть
      if (
        query.tab &&
        [
          "order",
          "trips",
          "cargos",
          "invoices",
          "edo_documents",
          "documents",
        ].includes(query.tab)
      ) {
        this.activeTab = query.tab;
      } else {
        // Если таба нет в URL, добавляем его
        this.updateUrlTab(this.activeTab);
      }
    },

    handleActions(action) {
      this.showDialog = false;

      if (action == "create") {
        this.createTrip();
      }
    },

    openRouteInNewTab,
    formatDateTimeLocal,
  },

  async created() {
    this.updateUrlTab(this.activeTab);

    await Promise.all([
      this.loadData(),
      this.orderStore.fetchOrderTypes(),
    ]);
  },
};
</script>

<style scoped>
.order-tabs-wrapper {
  margin-bottom: var(--base-out-indent);
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.block-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--base-in-indent);
}

.nav-actions {
  display: flex;
  gap: 15px;
}

.title-text {
  color: #666;
}

.order-block {
  margin-bottom: var(--base-out-indent);
}

.no-data-margin {
  margin-top: 40px;
}

.form-actions {
  margin-top: var(--base-in-indent);
}

@media (max-width: 768px) {
  .tab-button {
    padding: 10px 16px;
  }

}
</style>
